// Inline syntax (§11.1, reference: inline annotations, wiki-links, math, §12): a micromark
// extension, so CommonMark's precedence holds — code spans, escapes, link destinations and raw
// HTML come first, and none of these forms is ever read inside them.
//   :kind[label]{attrs}   typed references and annotations; after line start, whitespace or ( [ { < " '
//   [[target|label]]      a navigation link; ![[target|label]] an embed
//   [^label]              a footnote reference
//   $formula$             inline math: no space inside either dollar, no digit after the closer
//   ${name}               a parameter
// `inlineTokens` reads a Markdown source into these, with their offsets, for the reader's checks and
// the text profile; the renderer builds its tree from the same tokens.
import { parse, postprocess, preprocess } from "micromark";
import { gfmAutolinkLiteral } from "micromark-extension-gfm-autolink-literal";
import { gfmStrikethrough } from "micromark-extension-gfm-strikethrough";
import { gfmTable } from "micromark-extension-gfm-table";
import { gfmTaskListItem } from "micromark-extension-gfm-task-list-item";
import type { Code, Construct, Effects, Event, Extension, State, TokenizeContext } from "micromark-util-types";

declare module "micromark-util-types" {
  interface TokenTypeMap {
    imdDirective: "imdDirective";
    imdDirectiveMarker: "imdDirectiveMarker";
    imdDirectiveName: "imdDirectiveName";
    imdDirectiveLabel: "imdDirectiveLabel";
    imdDirectiveLabelMarker: "imdDirectiveLabelMarker";
    imdDirectiveLabelString: "imdDirectiveLabelString";
    imdDirectiveAttributes: "imdDirectiveAttributes";
    imdWiki: "imdWiki";
    imdEmbed: "imdEmbed";
    imdFootnoteRef: "imdFootnoteRef";
    imdFootnoteLabel: "imdFootnoteLabel";
    imdMath: "imdMath";
    imdMathMarker: "imdMathMarker";
    imdMathContent: "imdMathContent";
    imdParam: "imdParam";
    imdParamName: "imdParamName";
  }
}

const BANG = 33, QUOTE = 34, DOLLAR = 36, APOSTROPHE = 39, LPAREN = 40, DASH = 45, DOT = 46, COLON = 58, LT = 60;
const LBRACKET = 91, BACKSLASH = 92, RBRACKET = 93, CARET = 94, UNDERSCORE = 95, LBRACE = 123, RBRACE = 125, SPACE = 32;

const lower = (c: Code) => c !== null && c >= 97 && c <= 122;
const alpha = (c: Code) => c !== null && ((c >= 65 && c <= 90) || (c >= 97 && c <= 122));
const digit = (c: Code) => c !== null && c >= 48 && c <= 57;
const lineEnding = (c: Code) => c !== null && c < -2;
const space = (c: Code) => c === null || c < 0 || c === SPACE;

// ---- :kind[label]{attrs} ----

const directive: Construct = { name: "imdDirective", tokenize: tokenizeDirective, previous: directivePrevious };

function directivePrevious(this: TokenizeContext, code: Code): boolean {
  return space(code) || code === LPAREN || code === LBRACKET || code === LBRACE || code === LT || code === QUOTE || code === APOSTROPHE;
}

function tokenizeDirective(this: TokenizeContext, effects: Effects, ok: State, nok: State): State {
  let qualified = false;
  let balance = 0;
  return start;

  function start(code: Code): State | undefined {
    effects.enter("imdDirective");
    effects.enter("imdDirectiveMarker");
    effects.consume(code);
    effects.exit("imdDirectiveMarker");
    return nameStart;
  }
  function nameStart(code: Code): State | undefined {
    if (!lower(code)) return nok(code);
    effects.enter("imdDirectiveName");
    effects.consume(code);
    return name;
  }
  function name(code: Code): State | undefined {
    if (lower(code) || digit(code) || code === DASH) { effects.consume(code); return name; }
    if (code === COLON && !qualified) { qualified = true; effects.consume(code); return localStart; }
    if (code !== LBRACKET) return nok(code);
    effects.exit("imdDirectiveName");
    effects.enter("imdDirectiveLabel");
    effects.enter("imdDirectiveLabelMarker");
    effects.consume(code);
    effects.exit("imdDirectiveLabelMarker");
    return labelStart;
  }
  function localStart(code: Code): State | undefined {
    if (!lower(code)) return nok(code);
    effects.consume(code);
    return name;
  }
  function labelStart(code: Code): State | undefined {
    if (code === RBRACKET) return labelEnd(code);
    effects.enter("imdDirectiveLabelString");
    effects.enter("chunkText", { contentType: "text" });
    return label(code);
  }
  function label(code: Code): State | undefined {
    if (code === null || lineEnding(code)) return nok(code);
    if (code === LBRACKET) balance++;
    else if (code === RBRACKET) {
      if (balance === 0) {
        effects.exit("chunkText");
        effects.exit("imdDirectiveLabelString");
        return labelEnd(code);
      }
      balance--;
    }
    effects.consume(code);
    return code === BACKSLASH ? labelEscape : label;
  }
  function labelEscape(code: Code): State | undefined {
    if (code === LBRACKET || code === RBRACKET || code === BACKSLASH) { effects.consume(code); return label; }
    return label(code);
  }
  function labelEnd(code: Code): State | undefined {
    effects.enter("imdDirectiveLabelMarker");
    effects.consume(code);
    effects.exit("imdDirectiveLabelMarker");
    effects.exit("imdDirectiveLabel");
    return afterLabel;
  }
  function afterLabel(code: Code): State | undefined {
    return code === LBRACE ? effects.attempt(attributes, end, end)(code) : end(code);
  }
  function end(code: Code): State | undefined {
    effects.exit("imdDirective");
    return ok(code);
  }
}

/** `{…}` on the same line; a quoted value may hold a brace. */
const attributes: Construct = { tokenize: tokenizeAttributes, partial: true };

function tokenizeAttributes(this: TokenizeContext, effects: Effects, ok: State, nok: State): State {
  let quoted = false;
  return start;
  function start(code: Code): State | undefined {
    effects.enter("imdDirectiveAttributes");
    effects.consume(code);
    return inside;
  }
  function inside(code: Code): State | undefined {
    if (code === null || lineEnding(code)) return nok(code);
    if (quoted) {
      effects.consume(code);
      if (code === BACKSLASH) return escaped;
      if (code === QUOTE) quoted = false;
      return inside;
    }
    if (code === LBRACE) return nok(code);
    effects.consume(code);
    if (code === QUOTE) { quoted = true; return inside; }
    if (code === RBRACE) { effects.exit("imdDirectiveAttributes"); return ok; }
    return inside;
  }
  function escaped(code: Code): State | undefined {
    if (code === null || lineEnding(code)) return nok(code);
    effects.consume(code);
    return inside;
  }
}

// ---- [[target|label]] and ![[target|label]] ----

const wiki: Construct = { name: "imdWiki", tokenize: wikiTokenizer(false) };
const embed: Construct = { name: "imdEmbed", tokenize: wikiTokenizer(true) };

function wikiTokenizer(isEmbed: boolean) {
  return function tokenizeWiki(this: TokenizeContext, effects: Effects, ok: State, nok: State): State {
    const type = isEmbed ? "imdEmbed" : "imdWiki";
    let empty = true;
    return start;
    function start(code: Code): State | undefined {
      effects.enter(type);
      effects.consume(code);
      return isEmbed ? bang : second;
    }
    function bang(code: Code): State | undefined {
      if (code !== LBRACKET) return nok(code);
      effects.consume(code);
      return second;
    }
    function second(code: Code): State | undefined {
      if (code !== LBRACKET) return nok(code);
      effects.consume(code);
      return inside;
    }
    function inside(code: Code): State | undefined {
      if (code === null || lineEnding(code) || code === LBRACKET) return nok(code);
      if (code === RBRACKET) {
        if (empty) return nok(code);
        effects.consume(code);
        return close;
      }
      empty = false;
      effects.consume(code);
      return code === BACKSLASH ? escape : inside;
    }
    function escape(code: Code): State | undefined {
      if (code === null || lineEnding(code)) return nok(code);
      effects.consume(code);
      return inside;
    }
    function close(code: Code): State | undefined {
      if (code !== RBRACKET) return nok(code);
      effects.consume(code);
      return after;
    }
    function after(code: Code): State | undefined {
      // an ordinary link comes first: `[[x]](url)` is a link whose text is [x]
      if (code === LPAREN) return nok(code);
      effects.exit(type);
      return ok(code);
    }
  };
}

// ---- [^label] ----

const footnoteRef: Construct = { name: "imdFootnoteRef", tokenize: tokenizeFootnoteRef };

function tokenizeFootnoteRef(this: TokenizeContext, effects: Effects, ok: State, nok: State): State {
  return start;
  function start(code: Code): State | undefined {
    effects.enter("imdFootnoteRef");
    effects.consume(code);
    return caret;
  }
  function caret(code: Code): State | undefined {
    if (code !== CARET) return nok(code);
    effects.consume(code);
    return labelStart;
  }
  function labelStart(code: Code): State | undefined {
    if (!(alpha(code) || digit(code))) return nok(code);
    effects.enter("imdFootnoteLabel");
    effects.consume(code);
    return label;
  }
  function label(code: Code): State | undefined {
    if (alpha(code) || digit(code) || code === DOT || code === UNDERSCORE || code === DASH) { effects.consume(code); return label; }
    if (code !== RBRACKET) return nok(code);
    effects.exit("imdFootnoteLabel");
    effects.consume(code);
    return after;
  }
  function after(code: Code): State | undefined {
    if (code === LPAREN || code === COLON) return nok(code);
    effects.exit("imdFootnoteRef");
    return ok(code);
  }
}

// ---- ${name} and $formula$ ----

const param: Construct = { name: "imdParam", tokenize: tokenizeParam };

function tokenizeParam(this: TokenizeContext, effects: Effects, ok: State, nok: State): State {
  return start;
  function start(code: Code): State | undefined {
    effects.enter("imdParam");
    effects.consume(code);
    return brace;
  }
  function brace(code: Code): State | undefined {
    if (code !== LBRACE) return nok(code);
    effects.consume(code);
    return nameStart;
  }
  function nameStart(code: Code): State | undefined {
    if (!(alpha(code) || code === UNDERSCORE)) return nok(code);
    effects.enter("imdParamName");
    effects.consume(code);
    return name;
  }
  function name(code: Code): State | undefined {
    if (alpha(code) || digit(code) || code === UNDERSCORE || code === DOT || code === DASH) { effects.consume(code); return name; }
    if (code !== RBRACE) return nok(code);
    effects.exit("imdParamName");
    effects.consume(code);
    effects.exit("imdParam");
    return ok;
  }
}

const math: Construct = { name: "imdMath", tokenize: tokenizeMath };
/** The closer is not followed by a digit — which keeps `$5 and $10` ordinary text. */
const closer: Construct = { tokenize: tokenizeCloser, partial: true };

function tokenizeMath(this: TokenizeContext, effects: Effects, ok: State, nok: State): State {
  let previous: Code = null;
  return start;
  function start(code: Code): State | undefined {
    effects.enter("imdMath");
    effects.enter("imdMathMarker");
    effects.consume(code);
    effects.exit("imdMathMarker");
    return first;
  }
  function first(code: Code): State | undefined {
    if (space(code) || digit(code) || code === DOLLAR) return nok(code);
    effects.enter("imdMathContent");
    return inside(code);
  }
  function inside(code: Code): State | undefined {
    if (code === null || lineEnding(code)) return nok(code);
    if (code === DOLLAR && !space(previous)) return effects.check(closer, close, data)(code);
    return data(code);
  }
  function data(code: Code): State | undefined {
    const escape = code === BACKSLASH;
    previous = code;
    effects.consume(code);
    return escape ? escaped : inside;
  }
  function escaped(code: Code): State | undefined {
    if (code === null || lineEnding(code)) return nok(code);
    previous = code;
    effects.consume(code);
    return inside;
  }
  function close(code: Code): State | undefined {
    effects.exit("imdMathContent");
    effects.enter("imdMathMarker");
    effects.consume(code);
    effects.exit("imdMathMarker");
    effects.exit("imdMath");
    return ok;
  }
}

function tokenizeCloser(this: TokenizeContext, effects: Effects, ok: State, nok: State): State {
  return (code: Code) => {
    effects.enter("imdMathMarker");
    effects.consume(code);
    effects.exit("imdMathMarker");
    return (next: Code) => (digit(next) ? nok(next) : ok(next));
  };
}

/** The micromark syntax extension for instruction/1's inline forms. */
export function inlineSyntax(): Extension {
  return { text: { [COLON]: directive, [LBRACKET]: [footnoteRef, wiki], [BANG]: embed, [DOLLAR]: [param, math] } };
}

/** The base syntax the reader, the serializer and the renderer share: GFM's four productions (§4.1) and the inline forms. */
export function syntaxExtensions(): Extension[] {
  return [gfmTable(), gfmStrikethrough(), gfmTaskListItem(), gfmAutolinkLiteral(), inlineSyntax()];
}

// ---- reading tokens ----

export type InlineToken =
  | { type: "directive"; start: number; end: number; kind: string; label: string; labelStart: number; labelEnd: number; attributes: string | null }
  | { type: "wiki" | "embed"; start: number; end: number; target: string; label: string | null }
  | { type: "footnote"; start: number; end: number; label: string }
  | { type: "math"; start: number; end: number; formula: string }
  | { type: "param"; start: number; end: number; name: string };

/** The inline forms in a Markdown source, outermost first, each with its offsets into `source`. Read only: a source's
 *  tokens are kept (frozen) for the next time it is read, so an editor checking a document on every keystroke tokenizes
 *  only the prose that changed. */
export function inlineTokens(source: string): readonly InlineToken[] {
  // every form starts with one of these: without them there is nothing to find
  if (!/[:[$]/.test(source)) return NONE;
  const kept = memo.get(source);
  if (kept) { memo.delete(source); memo.set(source, kept); return kept; }
  // (micromark drops a byte-order mark that opens the text: the offsets index `source` as given)
  const bom = source.charCodeAt(0) === 0xfeff ? 1 : 0, text = bom ? source.slice(1) : source;
  const events = postprocess(parse({ extensions: syntaxExtensions() }).document().write(preprocess()(text, undefined, true)));
  const read = tokensFrom(events, text);
  const tokens = Object.freeze((bom ? read.map((t) => (t.type === "directive" ? { ...t, start: t.start + 1, end: t.end + 1, labelStart: t.labelStart + 1, labelEnd: t.labelEnd + 1 } : { ...t, start: t.start + 1, end: t.end + 1 })) : read).map((t) => Object.freeze(t)));
  memo.set(source, tokens);
  memoChars += source.length;
  // the oldest go first once the kept sources pass MEMO_CHARS
  for (const [k] of memo) { if (memoChars <= MEMO_CHARS) break; memo.delete(k); memoChars -= k.length; }
  return tokens;
}
const NONE: readonly InlineToken[] = Object.freeze([]);
/** The sources whose tokens are kept, least recently read first, and how many characters they hold together. */
const memo = new Map<string, readonly InlineToken[]>();
let memoChars = 0;
const MEMO_CHARS = 1_000_000;

export function tokensFrom(events: Event[], source: string): InlineToken[] {
  const out: InlineToken[] = [];
  type Directive = Extract<InlineToken, { type: "directive" }>;
  let current: Directive | null = null;
  const stack: Directive[] = [];
  let cells = 0;
  for (const [phase, token] of events) {
    const start = token.start.offset, end = token.end.offset;
    const slice = () => source.slice(start, end);
    if (token.type === "tableHeader" || token.type === "tableData") { cells += phase === "enter" ? 1 : -1; continue; }
    if (phase === "enter") {
      if (token.type === "imdDirective") {
        if (current) stack.push(current);
        current = { type: "directive", start, end, kind: "", label: "", labelStart: start, labelEnd: start, attributes: null };
        out.push(current);
      } else if (token.type === "imdWiki" || token.type === "imdEmbed") {
        const raw = slice();
        // in a table cell the separator pipe is escaped for the cell tokenizer, which removes that one layer
        const inner = raw.slice(token.type === "imdEmbed" ? 3 : 2, -2);
        const { target, label } = splitWiki(cells ? inner.replace(/\\\|/g, "|") : inner);
        out.push({ type: token.type === "imdEmbed" ? "embed" : "wiki", start, end, target, label });
      } else if (token.type === "imdFootnoteRef") out.push({ type: "footnote", start, end, label: slice().slice(2, -1) });
      else if (token.type === "imdMath") out.push({ type: "math", start, end, formula: slice().slice(1, -1) });
      else if (token.type === "imdParam") out.push({ type: "param", start, end, name: slice().slice(2, -1) });
      continue;
    }
    if (!current) continue;
    if (token.type === "imdDirectiveName") current.kind = slice();
    else if (token.type === "imdDirectiveLabelString") { current.label = slice(); current.labelStart = start; current.labelEnd = end; }
    else if (token.type === "imdDirectiveLabel" && !current.label) { current.labelStart = current.labelEnd = start + 1; }
    else if (token.type === "imdDirectiveAttributes") current.attributes = slice().slice(1, -1);
    else if (token.type === "imdDirective") current = stack.pop() ?? null;
  }
  return out;
}

/** `target|label`: the first unescaped pipe separates them; `\|` is a literal pipe. */
export function splitWiki(inner: string): { target: string; label: string | null } {
  for (let i = 0; i < inner.length; i++) {
    if (inner[i] === "\\") { i++; continue; }
    if (inner[i] === "|") return { target: unescapeWiki(inner.slice(0, i)), label: unescapeWiki(inner.slice(i + 1)) };
  }
  return { target: unescapeWiki(inner), label: null };
}

const unescapeWiki = (s: string) => s.replace(/\\([\\|\]\[])/g, "$1").trim();
